
import { computed, ref, onMounted, onBeforeUnmount } from "vue";

declare const faceapi: any;

export default {
    name: "UploadDocuments",
    props: ['baseUrl'],
    setup(props: any) {
        const loggedIn = ref(false);
        const documentsList = ref<string[]>([]);
        const iconUrls = ref<{ [key: string]: string }>({});
        const OfficerId = localStorage.OfficerId;

        // Face capture variables
        const videoRef = ref<HTMLVideoElement | null>(null);
        const canvasRef = ref<HTMLCanvasElement | null>(null);
        const cameraStarted = ref(false);
        const isProcessing = ref(false);
        const faceStatusMessage = ref('');
        const faceStatusClass = ref('');
        const savedFeatures = ref<number[] | null>(null);
        let mediaStream: MediaStream | null = null;
        const faceapiLoaded = ref(false);

        var Guardtoken = localStorage.getItem("Guardtoken")!;
        const baseUrlAddress = computed(() => {
            return `${props.baseUrl}`;
        });

        const getIconUrl = (documentName: string) => {
            return iconUrls.value[documentName] || null;
        };

        const loadIcons = () => {
            if (!OfficerId) {
                console.error("No OfficerId found");
                return;
            }

            console.log("Loading icons from:", props.baseUrl + 'SecurityOfficers/' + OfficerId + '/icons');

            fetch(props.baseUrl + 'SecurityOfficers/' + OfficerId + '/icons', {
                method: 'GET',
                headers: {
                    "Authorization": "Bearer " + localStorage.Guardtoken,
                },
            })
            .then(res => res.json())
            .then(data => {
                //console.log("Icon data:", data);
                
                const urlMap: { [key: string]: string } = {};
                
                if (Array.isArray(data)) {
                    data.forEach((url: string) => {
                        const match = url.match(/\/([^/?]+)\.(jpg|jpeg|png|gif|ico)/i);
                        if (match) {
                            const fileName = match[1];
                            urlMap[fileName] = url;
                            //console.log(`Mapped ${fileName} to URL`);
                        }
                    });
                }
                
                iconUrls.value = urlMap;
            })
            .catch(err => console.error(err));
        };

        const checkDocumentsStatus = () => {
            if (!OfficerId) return;
            
            fetch(props.baseUrl + 'SecurityOfficers/' + OfficerId + '/documents', {
                method: 'GET',
                headers: {
                    "Authorization": "Bearer " + localStorage.Guardtoken,
                },
            })
                .then(response => response.json())
                .then(data => {
                    documentsList.value = Array.isArray(data) ? data : [];
                })
                .catch(error => {
                    console.error(error);
                });
        };

        const isDocumentUploaded = (documentName: string) => {
            return documentsList.value.includes(documentName) || 
                   documentsList.value.includes(documentName + '.jpg');
        };

        const chooseDocument = (documentName: string) => {
            const formElement = document.getElementById(documentName + "Form") as HTMLFormElement;
            if (!formElement) return;
            
            const fileInput = formElement.querySelector('input[type="file"]') as HTMLInputElement;
            if (!fileInput || !fileInput.files || !fileInput.files[0]) {
                alert("Please select a file");
                return;
            }
            
            const formData = new FormData();
            formData.append('profile_pic', fileInput.files[0]);
            formData.append('documentName', documentName + '.jpg');

            fetch(props.baseUrl + 'SecurityOfficers/' + OfficerId + '/upload', {
                method: 'POST',
                headers: {
                    "Authorization": "Bearer " + localStorage.Guardtoken,
                },
                body: formData
            })
                .then(response => response.json())
                .then(() => {
                    checkDocumentsStatus();
                    loadIcons();
                    formElement.reset();
                })
                .catch(error => {
                    console.error(error);
                    alert("Upload failed");
                });
        };

        const token = localStorage.getItem("Guardtoken");

        if (token == null) {
            loggedIn.value = false;
        } else {
            loggedIn.value = true;
            checkDocumentsStatus();
            loadIcons();
        }

        const chooseDocumentPDF = (documentName: string) => {
            const formElement = document.getElementById(documentName + "Form") as HTMLFormElement;
            if (!formElement) return;
            
            const fileInput = formElement.querySelector('input[type="file"]') as HTMLInputElement;
            if (!fileInput || !fileInput.files || !fileInput.files[0]) {
                alert("Please select a PDF file");
                return;
            }
            
            const formData = new FormData();
            formData.append('profile_pic', fileInput.files[0]);
            formData.append('documentName', documentName + '.pdf');
            formData.append('isPdf', 'true');

            fetch(props.baseUrl + 'SecurityOfficers/' + OfficerId + '/upload', {
                method: 'POST',
                headers: {
                    "Authorization": "Bearer " + localStorage.Guardtoken,
                },
                body: formData
            })
                .then(response => response.json())
                .then(() => {
                    checkDocumentsStatus();
                    loadIcons();
                    formElement.reset();
                })
                .catch(error => {
                    console.error(error);
                    alert("Upload failed");
                });
        };

        const downloadDocument = (fileName: string) => {
            console.log("Downloading:", fileName);
        
            let extension = '';
            let downloadUrl = '';
            
            if (fileName === 'documents') {
                extension = '.pdf';
                downloadUrl = props.baseUrl + 'SecurityOfficers/' + OfficerId + '/download/' + fileName + extension;
            } else {
                extension = '.jpg';
                downloadUrl = props.baseUrl + 'SecurityOfficers/' + OfficerId + '/download/' + fileName + extension;
            }
            console.log("Download URL:", downloadUrl);
            
            fetch(downloadUrl, {
                method: 'GET',
                headers: {
                    "Authorization": "Bearer " + localStorage.Guardtoken,
                },
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Download failed: ${response.status}`);
                }
                return response.blob();
            })
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName + extension;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                console.log("Download completed for:", fileName + extension);
            })
            .catch(error => {
                console.error("Download error:", error);
                alert("Failed to download file. Please try again.");
            });
        };

        // Load face-api models
        const loadFaceApiModels = async () => {
            try {
                if (typeof faceapi === 'undefined') {
                    faceStatusMessage.value = 'Face detection library not loaded. Please refresh the page.';
                    faceStatusClass.value = 'text-danger';
                    console.error('face-api.js not loaded');
                    return false;
                }

                const MODEL_URL = '/models';
                await faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL);
                await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
                await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
                
                faceapiLoaded.value = true;
                console.log('Face-api models loaded');
                return true;
            } catch (error) {
                console.error('Failed to load face-api models:', error);
                faceStatusMessage.value = 'Failed to load face detection models';
                faceStatusClass.value = 'text-danger';
                return false;
            }
        };

        // Start camera
        const startCamera = async () => {
            try {
                if (!faceapiLoaded.value) {
                    faceStatusMessage.value = 'Loading face detection models...';
                    faceStatusClass.value = 'text-info';
                    const modelsLoaded = await loadFaceApiModels();
                    if (!modelsLoaded) {
                        return;
                    }
                }

                if (mediaStream) {
                    stopCamera();
                }

                faceStatusMessage.value = 'Requesting camera access...';
                mediaStream = await navigator.mediaDevices.getUserMedia({ 
                    video: { width: { ideal: 640 }, height: { ideal: 480 } } 
                });
                
                if (videoRef.value) {
                    videoRef.value.srcObject = mediaStream;
                    await videoRef.value.play();
                    cameraStarted.value = true;
                    faceStatusMessage.value = 'Camera ready. Click "Take Photo & Save" to capture your face.';
                    faceStatusClass.value = 'text-success';
                }
            } catch (error) {
                console.error('Camera error:', error);
                faceStatusMessage.value = 'Could not access camera. Please check permissions.';
                faceStatusClass.value = 'text-danger';
                cameraStarted.value = false;
            }
        };

        // Stop camera
        const stopCamera = () => {
            if (mediaStream) {
                mediaStream.getTracks().forEach(track => track.stop());
                mediaStream = null;
            }
            if (videoRef.value) {
                videoRef.value.srcObject = null;
            }
            cameraStarted.value = false;
            faceStatusMessage.value = 'Camera stopped.';
            faceStatusClass.value = 'text-warning';
        };

        // Extract face features from video
        const extractFaceFeatures = async (): Promise<number[] | null> => {
            const video = videoRef.value;
            if (!video || !video.videoWidth) {
                faceStatusMessage.value = 'Camera not ready';
                return null;
            }

            try {
                const detection = await faceapi
                    .detectSingleFace(video)
                    .withFaceLandmarks()
                    .withFaceDescriptor();

                if (!detection) {
                    faceStatusMessage.value = 'No face detected. Please ensure your face is clearly visible.';
                    return null;
                }

                // Draw detection on canvas for visual feedback
                const canvas = canvasRef.value;
                if (canvas) {
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;
                    canvas.style.display = 'block';
                    const displaySize = { width: video.videoWidth, height: video.videoHeight };
                    faceapi.matchDimensions(canvas, displaySize);
                    const resizedDetection = faceapi.resizeResults(detection, displaySize);
                    faceapi.draw.drawDetections(canvas, [resizedDetection]);
                    
                    setTimeout(() => {
                        if (canvas) canvas.style.display = 'none';
                    }, 1000);
                }

                faceStatusMessage.value = 'Face detected! Features extracted successfully.';
                faceStatusClass.value = 'text-success';
                return Array.from(detection.descriptor);
            } catch (error) {
                console.error('Face extraction error:', error);
                faceStatusMessage.value = 'Error extracting face features';
                return null;
            }
        };

        // Save facial features to API
        const saveFacialFeaturesToApi = async (features: number[]): Promise<boolean> => {
            try {
                console.log('Saving facial features for officer:', OfficerId);
                console.log('API URL:', `${baseUrlAddress.value}SecurityOfficers/${OfficerId}/SaveFacialFeature`);

                const response = await fetch(`${baseUrlAddress.value}SecurityOfficers/${OfficerId}/SaveFacialFeature`, {
                    method: "POST",
                    headers: {
                        "Authorization": `Bearer ${Guardtoken}`,
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({ 
                        FacialFeatures: features
                    })
                });

                console.log('Response status:', response.status);

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('API Error:', errorText);
                    throw new Error(`API returned ${response.status}`);
                }

                const result = await response.json();
                console.log("Save facial features result:", result);
                return result.success === true;
            } catch (error) {
                console.error('Error saving facial features:', error);
                return false;
            }
        };

        // Main function to capture and save face features
        const captureAndSaveFaceFeatures = async () => {
            console.log("captureAndSaveFaceFeatures called");
            
            if (!cameraStarted.value) {
                faceStatusMessage.value = 'Please start the camera first.';
                faceStatusClass.value = 'text-warning';
                return;
            }

            isProcessing.value = true;
            faceStatusMessage.value = 'Extracting face features...';
            faceStatusClass.value = 'text-info';

            const features = await extractFaceFeatures();
            
            if (!features) {
                faceStatusMessage.value = 'No face detected. Please try again.';
                faceStatusClass.value = 'text-danger';
                isProcessing.value = false;
                return;
            }
            
            faceStatusMessage.value = 'Saving facial features to server...';
            const saved = await saveFacialFeaturesToApi(features);
            
            if (saved) {
                savedFeatures.value = features;
                faceStatusMessage.value = 'Face features saved successfully!';
                faceStatusClass.value = 'text-success';
            } else {
                faceStatusMessage.value = 'Failed to save face features. Please try again.';
                faceStatusClass.value = 'text-danger';
            }
            
            isProcessing.value = false;
        };

        // Clean up on component unmount
        onBeforeUnmount(() => {
            stopCamera();
        });

        return {
            loggedIn,
            chooseDocument,
            documentsList,
            isDocumentUploaded,
            OfficerId,
            checkDocumentsStatus,
            getIconUrl,
            iconUrls,
            loadIcons,
            downloadDocument,
            chooseDocumentPDF,
            // Camera face capture
            videoRef,
            canvasRef,
            cameraStarted,
            isProcessing,
            faceStatusMessage,
            faceStatusClass,
            savedFeatures,
            startCamera,
            stopCamera,
            captureAndSaveFaceFeatures,
        };
    }
};
